<template>
  <div class="space-y-4">
    <div v-if="mode === 'data'" class="grid gap-4 md:grid-cols-2">
      <DatepickerField name="date" :label="t('date')" :enable-time-picker="true" />
      <InputField name="licencePlate" empty-value-to="undefined" :label="t('licencePlate')" />
      <InputField
        name="documentLanguage"
        empty-value-to="undefined"
        :label="t('documentLanguage')"
      />
      <SearchableSelectField
        name="country"
        :label="t('country')"
        :options="countries"
        :track-by="['label', 'labelEn', 'labelShort', 'value', 'alternativeNames']"
      />
      <InputField name="authority" empty-value-to="undefined" :label="t('authority')" />
      <InputField name="email" empty-value-to="undefined" :label="t('email')" />
      <InputField name="fileNumber" empty-value-to="undefined" :label="t('fileNumber')" />
      <SelectField
        name="infringementType"
        empty-value-to="undefined"
        :label="t('infringementType')"
        :options="infringementTypeOptions"
      />
    </div>

    <div v-else class="grid gap-4 md:grid-cols-2">
      <Select v-model="bookingSource" :label="t('bookingSource')" :options="bookingSourceOptions" />
      <SearchBookingInput
        v-if="bookingSource === 'ros'"
        :label="t('rosBooking')"
        @update-booking="setFieldValue('rosBookingId', $event?.id ?? '')"
      />
      <InputField
        v-else
        name="wunderRentalId"
        :label="t('manualWunderRentalId')"
        type="number"
        :use-grouping="false"
      />
    </div>

    <div class="flex justify-end gap-4">
      <CVButton variant="default" :is-loading="isLoading" @click.prevent="onSubmit">
        {{ t('save') }}
      </CVButton>
      <CVButton @click.prevent="$emit('cancel')">{{ t('cancel') }}</CVButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import type {
  Infringement,
  InfringementData,
  UpdateInfringementDto,
} from '@carvia/ros-client-types';
import { InfringementType } from '@carvia/ros-client-types';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import Select from '@/components/Select.vue';
import SearchableSelectField from '@/components/SearchableSelectField.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import DatepickerField from '@/components/DatepickerField.vue';
import SearchBookingInput from '@/components/booking-forms/SearchBookingInput.vue';
import { getCountries } from '@/utils/countries';
import * as yup from 'yup';

const props = defineProps<{
  infringement: Infringement;
  mode: 'data' | 'booking';
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'submit', payload: UpdateInfringementDto): void;
  (e: 'cancel'): void;
}>();

const { t, locale } = useI18n();
const countries = getCountries(locale.value);

const getInfringementCountry = (data: Infringement['data']) =>
  (data as (InfringementData & { country?: string }) | null)?.country;

const createInitialValues = () =>
  props.mode === 'data'
    ? {
        authority: props.infringement.data?.authority ?? undefined,
        email: props.infringement.data?.email ?? undefined,
        date: props.infringement.data?.date ?? '',
        country: getInfringementCountry(props.infringement.data) ?? undefined,
        documentLanguage: props.infringement.data?.documentLanguage ?? undefined,
        fileNumber: props.infringement.data?.fileNumber ?? '',
        infringementType: props.infringement.data?.infringementType ?? InfringementType.OTHER,
        licencePlate: props.infringement.data?.licencePlate ?? '',
      }
    : {
        rosBookingId: props.infringement.rosBooking?.id ?? null,
        wunderRentalId: props.infringement.wunderBooking?.rentalId ?? null,
      };

const bookingSource = ref<'ros' | 'wunder'>(props.infringement.rosBooking ? 'ros' : 'wunder');

const { handleSubmit, resetForm, setFieldValue } = useForm<ReturnType<typeof createInitialValues>>({
  initialValues: createInitialValues(),
  validationSchema: computed(() =>
    props.mode === 'data'
      ? {
          authority: yup.string().trim().nullable().optional(),
          email: yup.string().trim().email().nullable().optional(),
          date: yup.string().trim().nullable().optional(),
          country: yup
            .string()
            .trim()
            .matches(/^[A-Z]{2}$/, t('countryValidation'))
            .nullable()
            .optional(),
          documentLanguage: yup
            .string()
            .trim()
            .matches(/^[a-z]{2}$/, t('documentLanguageValidation'))
            .nullable()
            .optional(),
          fileNumber: yup.string().trim().nullable().optional(),
          infringementType: yup
            .mixed<InfringementType>()
            .oneOf(Object.values(InfringementType))
            .nullable()
            .optional(),
          licencePlate: yup.string().trim().nullable().optional(),
        }
      : {
          rosBookingId:
            bookingSource.value === 'ros'
              ? yup.string().uuid().required()
              : yup.string().optional().nullable(),
          wunderRentalId:
            bookingSource.value === 'wunder'
              ? yup.number().integer().positive().required()
              : yup.number().optional().nullable(),
        },
  ),
});

watch(
  () => props.infringement,
  () => {
    resetForm({ values: createInitialValues() });
  },
);

const onSubmit = handleSubmit((values) => {
  if (props.mode === 'data') {
    emit('submit', {
      data: values as InfringementData,
    });
    return;
  }
  emit('submit', {
    rosBookingId: bookingSource.value === 'ros' ? values.rosBookingId || undefined : undefined,
    wunderRentalId:
      bookingSource.value === 'wunder' ? values.wunderRentalId || undefined : undefined,
  });
});

const infringementTypeOptions = computed(() =>
  Object.values(InfringementType).map((type) => ({
    label: t(`infringementTypes.${type}`),
    value: type,
  })),
);
const bookingSourceOptions = computed(() => [
  { label: t('bookingSourceRos'), value: 'ros' },
  { label: t('bookingSourceWunder'), value: 'wunder' },
]);
</script>

<i18n lang="json">
{
  "en": {
    "authority": "Authority",
    "documentLanguage": "Document Language ('de', 'en', etc.)",
    "documentLanguageValidation": "Please provide a 2-letter lowercase language code (e.g. de, en).",
    "country": "Country",
    "countryValidation": "Please provide a valid alpha-2 country code (e.g. DE).",
    "email": "Email",
    "date": "Date",
    "fileNumber": "File Number",
    "infringementType": "Infringement Type",
    "bookingSource": "Booking source",
    "bookingSourceRos": "ROS booking",
    "bookingSourceWunder": "Wunder rental",
    "rosBooking": "ROS booking",
    "manualWunderRentalId": "Wunder rental ID"
  },
  "de": {
    "authority": "Behörde",
    "documentLanguage": "Dokumentsprache ('de', 'en', etc.)",
    "documentLanguageValidation": "Bitte einen zweistelligen Sprachcode in Kleinbuchstaben angeben (z. B. de, en).",
    "country": "Land",
    "countryValidation": "Bitte einen gültigen Alpha-2-Ländercode angeben (z. B. DE).",
    "email": "E-Mail",
    "date": "Datum",
    "fileNumber": "Aktenzeichen",
    "infringementType": "Ordnungswidrigkeitstyp",
    "bookingSource": "Buchungsquelle",
    "bookingSourceRos": "ROS Buchung",
    "bookingSourceWunder": "Wunder Miete",
    "rosBooking": "ROS Buchung",
    "manualWunderRentalId": "Wunder Miet-ID"
  }
}
</i18n>
